import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconChevronDown, IconChevronUp, IconMenu } from "../icons";
import AppButton from "./AppButton";
import AppCheckbox from "./AppCheckbox";
import AppInput from "./AppInput";
import AppLoadingOverlay from "./AppLoadingOverlay";
import AppText from "./AppText";

const DEFAULT_COLUMN_MIN_WIDTH = 160;
const DEFAULT_COLUMN_GROW = 1;
const DEFAULT_COLUMN_ALIGN = "left";
const CONTROL_COLUMN_FIT_CAP = 64;
const CONTROL_COLUMN_MIN_TRACK_WIDTH = 64;
const CONTROL_COLUMN_ALIGN = "center";

function AppTableFooterMeta({ children, className = "" }) {
  const classes = ["ds-table__footer-meta", className].filter(Boolean).join(" ");
  return <div className={classes}>{children}</div>;
}

function AppTableFooterSummary({ children, className = "" }) {
  const classes = ["ds-table__footer-summary", className].filter(Boolean).join(" ");
  return <div className={classes}>{children}</div>;
}

function resolveControlKind(column) {
  return column.controlKind || null;
}

function getDefaultColumnAlign(column) {
  return resolveControlKind(column) ? CONTROL_COLUMN_ALIGN : DEFAULT_COLUMN_ALIGN;
}

function getDefaultColumnGrow(column) {
  return resolveControlKind(column) ? 0 : DEFAULT_COLUMN_GROW;
}

function getDefaultColumnMinWidth(column) {
  return resolveControlKind(column) ? CONTROL_COLUMN_MIN_TRACK_WIDTH : DEFAULT_COLUMN_MIN_WIDTH;
}

function getResolvedColumnMinWidth(column) {
  if (typeof column.minWidth === "number") {
    return column.minWidth;
  }

  if (typeof column.width === "number") {
    return column.width;
  }

  return getDefaultColumnMinWidth(column);
}

function getResolvedColumnMaxWidth(column) {
  if (typeof column.maxWidth === "number") {
    return column.maxWidth;
  }

  if (resolveControlKind(column) && column.width === undefined) {
    return CONTROL_COLUMN_FIT_CAP;
  }

  return null;
}

function getResolvedColumnGrow(column) {
  return typeof column.grow === "number" ? column.grow : getDefaultColumnGrow(column);
}

function getResolvedStaticColumnWidth(column) {
  if (column.width !== undefined && column.width !== null) {
    return typeof column.width === "number" ? column.width : null;
  }

  const grow = getResolvedColumnGrow(column);
  if (grow > 0) {
    return null;
  }

  const minWidth = getResolvedColumnMinWidth(column);
  const maxWidth = getResolvedColumnMaxWidth(column);
  if (typeof maxWidth === "number") {
    return Math.max(maxWidth, minWidth);
  }

  return minWidth;
}

function getCellAlign(column) {
  if (column.align) {
    return column.align;
  }

  return getDefaultColumnAlign(column);
}

function blocksPrimaryRowAction(controlKind) {
  return controlKind === "selection";
}

function renderEmptyState() {
  return <span className="ds-table__empty">-</span>;
}

function formatEntryLabel(count) {
  return `${count} ${count === 1 ? "Eintrag" : "Einträge"}`;
}

function formatEntryLabelAfterVon(count) {
  return `${count} ${count === 1 ? "Eintrag" : "Einträgen"}`;
}

function getColumnTrack(column) {
  if (column.width !== undefined && column.width !== null) {
    return typeof column.width === "number" ? `${column.width}px` : column.width;
  }

  const staticWidth = getResolvedStaticColumnWidth(column);
  if (typeof staticWidth === "number") {
    return `${staticWidth}px`;
  }

  const minWidth = getResolvedColumnMinWidth(column);
  const grow = getResolvedColumnGrow(column);

  return `minmax(${minWidth}px, ${grow}fr)`;
}

function getColumnMinTrackWidth(column) {
  if (typeof column.width === "number") {
    return column.width;
  }

  if (typeof column.width === "string") {
    const minmaxMatch = column.width.match(/^minmax\(\s*(\d+(?:\.\d+)?)px\s*,/i);
    if (minmaxMatch) {
      return Number(minmaxMatch[1]);
    }

    const pxMatch = column.width.match(/^(\d+(?:\.\d+)?)px$/i);
    if (pxMatch) {
      return Number(pxMatch[1]);
    }

  }

  const staticWidth = getResolvedStaticColumnWidth(column);
  if (typeof staticWidth === "number") {
    return staticWidth;
  }

  return getResolvedColumnMinWidth(column);
}

function getSortValue(column, row) {
  if (typeof column.sortAccessor === "function") {
    return column.sortAccessor(row);
  }

  const value = row[column.key];

  if (value === undefined || value === null) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  if (typeof value === "object") {
    if (typeof value.value === "number") {
      return value.value;
    }
    if (typeof value.label === "string") {
      return value.label;
    }
  }

  return String(value);
}

function compareSortValues(leftValue, rightValue) {
  const leftIsNumber = typeof leftValue === "number";
  const rightIsNumber = typeof rightValue === "number";

  if (leftIsNumber && rightIsNumber) {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), "de", { numeric: true, sensitivity: "base" });
}

function getNextSortDirection(currentSort, columnKey) {
  if (currentSort.key !== columnKey) {
    return "asc";
  }

  if (currentSort.direction === "asc") {
    return "desc";
  }

  if (currentSort.direction === "desc") {
    return null;
  }

  return "asc";
}

function SortIcon({ direction }) {
  if (!direction) {
    return null;
  }

  return (
    <span className="ds-table__sort-icon-stack" aria-hidden="true">
      <IconChevronUp
        className={`ds-table__sort-icon${direction === "desc" ? " ds-table__sort-icon--active" : ""}`}
        sx={{ fontSize: 14 }}
      />
      <IconChevronDown
        className={`ds-table__sort-icon${direction === "asc" ? " ds-table__sort-icon--active" : ""}`}
        sx={{ fontSize: 14 }}
      />
    </span>
  );
}

function renderCellContent(column, value, row, density) {
  if (typeof column.render === "function") {
    return column.render(value, row);
  }

  if (resolveControlKind(column) === "menu") {
    return (
      <AppButton
        variant="neutral"
        size="sm"
        iconOnly
        className="ds-table__menu-button"
        aria-label="Zeilenmenü"
        onClick={(event) => event.stopPropagation()}
      >
        <IconMenu sx={{ fontSize: 18 }} />
      </AppButton>
    );
  }

  if (!value) {
    return renderEmptyState();
  }

  return (
    <AppText variant={density === "compact" ? "body-2" : "body-1"} tone="primary" truncate>
      {value}
    </AppText>
  );
}

function AppTable({
  columns = [],
  rows = [],
  footer = null,
  totalRowCount,
  showFooter = true,
  loading = false,
  emptyState = "Keine Einträge vorhanden",
  className = "",
  density = "default",
  ariaLabel,
  ariaLabelledBy,
  selectedRowIds,
  defaultSelectedRowIds = [],
  onSelectedRowIdsChange,
  isRowSelectable = () => true,
  getRowId = (row) => row.id,
  getRowClassName,
  onRowClick,
  activeRowId = null,
  activeRowHoverAccent = false,
  onCellEditStart,
  onCellEditCommit,
  onCellEditCancel,
}) {
  const [sortState, setSortState] = useState({ key: null, direction: null });
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [internalSelectedRowIds, setInternalSelectedRowIds] = useState(defaultSelectedRowIds);
  const [editingCell, setEditingCell] = useState(null);
  const bodyRef = useRef(null);
  const classes = [
    "ds-table",
    density === "compact" ? "ds-table--compact" : "",
    activeRowHoverAccent ? "ds-table--active-row-hover-accent" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const hasSelectionColumn = columns.some((column) => resolveControlKind(column) === "selection");
  const isSelectionControlled = selectedRowIds !== undefined;
  const selectionIsInteractive =
    hasSelectionColumn &&
    (isSelectionControlled || typeof onSelectedRowIdsChange === "function" || defaultSelectedRowIds.length > 0);
  const effectiveSelectedRowIds = useMemo(
    () => (isSelectionControlled ? selectedRowIds || [] : internalSelectedRowIds),
    [internalSelectedRowIds, isSelectionControlled, selectedRowIds]
  );
  const gridTemplateColumns = useMemo(() => columns.map((column) => getColumnTrack(column)).join(" "), [columns]);
  const minTableWidth = useMemo(
    () => columns.reduce((sum, column) => sum + getColumnMinTrackWidth(column), 0),
    [columns]
  );
  const sortedRows = useMemo(() => {
    if (!sortState.key || !sortState.direction) {
      return rows;
    }

    const activeColumn = columns.find((column) => column.key === sortState.key);
    if (!activeColumn) {
      return rows;
    }

    return [...rows].sort((leftRow, rightRow) => {
      const result = compareSortValues(getSortValue(activeColumn, leftRow), getSortValue(activeColumn, rightRow));
      return sortState.direction === "asc" ? result : -result;
    });
  }, [columns, rows, sortState]);
  const selectableRows = useMemo(
    () => sortedRows.filter((row) => isRowSelectable(row)),
    [isRowSelectable, sortedRows]
  );
  const selectedRowIdSet = useMemo(() => {
    if (selectionIsInteractive) {
      return new Set(effectiveSelectedRowIds);
    }

    return new Set(rows.filter((row) => Boolean(row.selected)).map((row) => getRowId(row)));
  }, [effectiveSelectedRowIds, getRowId, rows, selectionIsInteractive]);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedRowIdSet.has(getRowId(row)));
  const hasSelectedRows = selectableRows.some((row) => selectedRowIdSet.has(getRowId(row)));
  const headerCheckboxIndeterminate = hasSelectedRows && !allSelected;
  const hasRows = sortedRows.length > 0;
  const selectedCount = selectedRowIdSet.size;
  const resolvedTotalRowCount =
    typeof totalRowCount === "number" && Number.isFinite(totalRowCount) ? Math.max(totalRowCount, rows.length) : rows.length;
  const isFilteredRowSet = resolvedTotalRowCount > rows.length;
  const defaultFooter = showFooter ? (
    <>
      {selectedCount > 0 ? (
        <AppTableFooterMeta>
          <span>{`${selectedCount} ausgewählt`}</span>
        </AppTableFooterMeta>
      ) : null}
      <AppTableFooterSummary>
        <span>
          {isFilteredRowSet
            ? `${rows.length} von ${formatEntryLabelAfterVon(resolvedTotalRowCount)}`
            : formatEntryLabel(rows.length)}
        </span>
      </AppTableFooterSummary>
    </>
  ) : null;
  const resolvedFooter = footer ?? defaultFooter;
  const hasFooter = Boolean(resolvedFooter);

  function updateSelectedRowIds(nextSelectedRowIds) {
    if (!isSelectionControlled) {
      setInternalSelectedRowIds(nextSelectedRowIds);
    }

    onSelectedRowIdsChange?.(nextSelectedRowIds);
  }

  function handleRowSelectionChange(row, checked) {
    if (!isRowSelectable(row)) {
      return;
    }

    const rowId = getRowId(row);
    const nextSelectedRowIds = checked
      ? [...effectiveSelectedRowIds, rowId]
      : effectiveSelectedRowIds.filter((selectedId) => selectedId !== rowId);

    updateSelectedRowIds(nextSelectedRowIds);
  }

  function handleToggleAllRows(checked) {
    const nextSelectedRowIds = checked
      ? selectableRows.map((row) => getRowId(row))
      : [];
    updateSelectedRowIds(nextSelectedRowIds);
  }

  function isCellEditable(column, row) {
    return typeof column.editable === "function" ? column.editable(row) : Boolean(column.editable);
  }

  function startCellEdit(column, row) {
    if (!isCellEditable(column, row)) {
      return;
    }

    const rowId = getRowId(row);
    const value = row[column.key];
    const canStart = onCellEditStart?.({ id: rowId, field: column.key, value, row });

    if (canStart === false) {
      return;
    }

    setEditingCell({ rowId, columnKey: column.key, originalValue: value, value });
  }

  function commitCellEdit(nextValue) {
    if (!editingCell) {
      return;
    }

    const committedCell = editingCell;
    const value = nextValue === undefined ? committedCell.value : nextValue;
    const row = rows.find((candidateRow) => getRowId(candidateRow) === committedCell.rowId);
    setEditingCell(null);
    onCellEditCommit?.({
      id: committedCell.rowId,
      field: committedCell.columnKey,
      value,
      originalValue: committedCell.originalValue,
      row,
    });
  }

  function cancelCellEdit() {
    if (!editingCell) {
      return;
    }

    const cancelledCell = editingCell;
    setEditingCell(null);
    onCellEditCancel?.({
      id: cancelledCell.rowId,
      field: cancelledCell.columnKey,
      value: cancelledCell.originalValue,
    });
  }

  useEffect(() => {
    const bodyNode = bodyRef.current;
    if (!bodyNode) {
      return undefined;
    }

    const measureScrollbar = () => {
      setScrollbarWidth(bodyNode.offsetWidth - bodyNode.clientWidth);
    };

    measureScrollbar();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        measureScrollbar();
      });
      resizeObserver.observe(bodyNode);
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", measureScrollbar);
    }

    return () => {
      resizeObserver?.disconnect();
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", measureScrollbar);
      }
    };
  }, [columns.length, rows.length, hasFooter]);

  useEffect(() => {
    if (!editingCell) {
      return;
    }

    const rowStillExists = rows.some((row) => getRowId(row) === editingCell.rowId);
    const columnStillExists = columns.some((column) => column.key === editingCell.columnKey);
    if (!rowStillExists || !columnStillExists) {
      setEditingCell(null);
    }
  }, [columns, editingCell, getRowId, rows]);

  return (
    <div
      className={classes}
      role="table"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      <div className="ds-table__header">
        <div
          className="ds-table__header-inner"
          style={{
            gridTemplateColumns,
            width: `max(100%, ${minTableWidth}px)`,
            transform: `translateX(-${scrollLeft}px)`,
            paddingRight: scrollbarWidth ? `${scrollbarWidth}px` : undefined,
          }}
          role="row"
        >
          {columns.map((column) => {
            const align = getCellAlign(column);
            const controlKind = resolveControlKind(column);
            const isSortable = column.sortable !== false && controlKind !== "selection" && controlKind !== "menu";
            const sortDirection = sortState.key === column.key ? sortState.direction : null;
            return (
              <div
                key={column.key}
                role="columnheader"
                className={`ds-table__head ds-table__head--${align}${
                  controlKind === "selection" ? " ds-table__head--selection" : ""
                }`}
              >
                {controlKind === "selection" ? (
                  <span className="ds-table__checkbox-wrap">
                    <AppCheckbox
                      checked={allSelected}
                      indeterminate={headerCheckboxIndeterminate}
                      readOnly={!selectionIsInteractive}
                      disabled={!selectionIsInteractive || selectableRows.length === 0}
                      aria-label="Alle Zeilen auswählen"
                      onChange={(event) => {
                        if (!selectionIsInteractive) {
                          return;
                        }

                        handleToggleAllRows(Boolean(event.target.checked));
                      }}
                    />
                  </span>
                ) : isSortable ? (
                  <button
                    type="button"
                    className={`ds-table__sort-button${sortDirection ? " is-active" : ""}`}
                    onClick={() => {
                      const nextDirection = getNextSortDirection(sortState, column.key);
                      setSortState({ key: nextDirection ? column.key : null, direction: nextDirection });
                    }}
                    aria-label={`${column.label} sortieren`}
                  >
                    <span className="ds-table__sort-label">{column.label}</span>
                    <SortIcon direction={sortDirection} />
                  </button>
                ) : (
                  column.label
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div
        ref={bodyRef}
        className={`ds-table__body${hasRows ? "" : " ds-table__body--empty"}`}
        onScroll={(event) => {
          setScrollLeft(event.currentTarget.scrollLeft);
        }}
        role="rowgroup"
      >
        <div
          className="ds-table__body-inner"
          style={{ width: hasRows ? `max(100%, ${minTableWidth}px)` : "100%" }}
        >
          {hasRows ? (
            sortedRows.map((row) => {
              const rowId = getRowId(row);
              const isActive = activeRowId !== null && activeRowId !== undefined && rowId === activeRowId;
              const rowClassName = typeof getRowClassName === "function" ? getRowClassName(row) : "";

              return (
                <div
                  key={rowId}
                  className={`ds-table__row${typeof onRowClick === "function" ? " ds-table__row--clickable" : ""}${
                    isActive ? " ds-table__row--active" : ""
                  }${rowClassName ? ` ${rowClassName}` : ""}`}
                  style={{ gridTemplateColumns }}
                  role="row"
                  tabIndex={typeof onRowClick === "function" ? 0 : undefined}
                  onClick={typeof onRowClick === "function" ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    typeof onRowClick === "function"
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((column) => {
                    const align = getCellAlign(column);
                    const controlKind = resolveControlKind(column);
                    const cellBlocksPrimaryRowAction = blocksPrimaryRowAction(controlKind);
                    const cellIsEditable = isCellEditable(column, row);
                    const cellIsEditing =
                      editingCell?.rowId === rowId && editingCell?.columnKey === column.key;
                    return (
                      <div
                        key={column.key}
                        role="cell"
                        className={`ds-table__cell ds-table__cell--${align}${
                          cellIsEditable ? " ds-table__cell--editable" : ""
                        }${cellIsEditing ? " ds-table__cell--editing" : ""}`}
                        onClick={cellBlocksPrimaryRowAction ? (event) => event.stopPropagation() : undefined}
                        onDoubleClick={cellIsEditable ? () => startCellEdit(column, row) : undefined}
                        onKeyDown={
                          cellIsEditing
                            ? (event) => {
                                if (event.key === "Escape") {
                                  event.preventDefault();
                                  cancelCellEdit();
                                } else if (event.key === "Enter") {
                                  event.preventDefault();
                                  commitCellEdit();
                                }
                              }
                            : cellIsEditable
                              ? (event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    startCellEdit(column, row);
                                  }
                                }
                              : undefined
                        }
                        tabIndex={cellIsEditable && !cellIsEditing ? 0 : undefined}
                      >
                        <div
                          className={`ds-table__cell-content ds-table__cell-content--${align}`}
                          onBlur={
                            cellIsEditing
                              ? (event) => {
                                  if (!event.currentTarget.contains(event.relatedTarget)) {
                                    commitCellEdit();
                                  }
                                }
                              : undefined
                          }
                        >
                          {controlKind === "selection" ? (
                            <span className="ds-table__checkbox-wrap" onClick={(event) => event.stopPropagation()}>
                              <AppCheckbox
                                checked={selectedRowIdSet.has(rowId)}
                                readOnly={!selectionIsInteractive}
                                disabled={!selectionIsInteractive || !isRowSelectable(row)}
                                aria-label={`Zeile ${rowId} auswählen`}
                                onChange={(event) => {
                                  if (!selectionIsInteractive) {
                                    return;
                                  }

                                  handleRowSelectionChange(row, Boolean(event.target.checked));
                                }}
                              />
                            </span>
                          ) : cellIsEditing ? (
                            typeof column.renderEdit === "function" ? (
                              column.renderEdit(editingCell.value, row, {
                                onChange: (value) => setEditingCell((current) => ({ ...current, value })),
                                onCommit: commitCellEdit,
                                onCancel: cancelCellEdit,
                              })
                            ) : (
                              <AppInput
                                autoFocus
                                size="sm"
                                value={editingCell.value ?? ""}
                                onChange={(event) =>
                                  setEditingCell((current) => ({ ...current, value: event.target.value }))
                                }
                              />
                            )
                          ) : (
                            renderCellContent(column, row[column.key], row, density)
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div
              className="ds-table__row ds-table__row--empty"
              style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
              role="row"
            >
              <div className="ds-table__cell ds-table__cell--empty ds-table__cell--empty-state" role="cell">
                <div className="ds-table__empty-state">{emptyState}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {hasFooter ? (
        <div className="ds-table__footer-shell">
          <div className="ds-table__footer">{resolvedFooter}</div>
        </div>
      ) : null}

      {loading ? <AppLoadingOverlay open scope="contained" /> : null}
    </div>
  );
}

AppTable.FooterMeta = AppTableFooterMeta;
AppTable.FooterSummary = AppTableFooterSummary;

export {
  AppTableFooterMeta,
  AppTableFooterSummary,
};

export default AppTable;
